// Match Day (2026-10-18, staging only) — the trainer's touchline page on the iPad: sub log with
// exact times, live minutes per player, and the HID top-up. Full-screen route /matchday (outside
// the app shell, like /board), reached from the 'Match Day' tab. Offline-first: see
// matchdayStore.js. Server side: services/matchday/.
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Cloud, CloudOff, CheckCircle, AlertTriangle } from 'lucide-react';
import { getMatchdayFixtures } from '../../api';
import { computeMinutes } from './engine';
import { useMatchday, loadIndex, loadLocal, dirtyCount, hasSessionToken } from './matchdayStore';
import MatchTab from './MatchTab';
import SquadTab from './SquadTab';
import TopUpTab from './TopUpTab';
import { Btn, Badge, C, useNow, useWakeLock, fmtDate, fmtKickoff, fmtTime } from './ui';

export const canUseMatchday = user =>
  !!user && user.role !== 'player' && user.role !== 'chef' && (user.isAdmin || (user.allowedTabs || []).includes('matchday'));

function Page({ children }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-app)', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif', padding: '16px 16px 24px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>{children}</div>
    </div>
  );
}

function SessionWarning() {
  if (hasSessionToken()) return null;
  return (
    <div style={{ padding: 12, borderRadius: 12, border: `1px solid ${C.red}`, color: C.red, fontWeight: 700, marginBottom: 12 }}>
      This login has no server session (it predates session tokens). Log out and log in again before the match, or nothing will sync.
    </div>
  );
}

function FixturePicker({ onPick, onExit }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    getMatchdayFixtures().then(setData).catch(err => setError(err.message));
  }, []);
  const prepared = loadIndex();
  const fixtures = data?.fixtures || prepared.map(p => ({ ...p, offlineOnly: true }));
  const today = data?.today || new Date().toISOString().slice(0, 10);

  return (
    <Page>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Btn tone="ghost" onClick={onExit}><ArrowLeft size={18} /> App</Btn>
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0 }}>Match Day</h1>
      </div>
      <SessionWarning />
      {error && (
        <div style={{ color: C.amber, fontWeight: 600, marginBottom: 12 }}>
          {/API error: 403/.test(error) ? 'This account has no Match Day access.' : `Fixtures could not be loaded (${error}).`}
          {prepared.length ? ' Showing the matches prepared on this device.' : ''}
        </div>
      )}
      {!data && !error && <div style={{ color: 'var(--text-muted)' }}>Loading fixtures…</div>}
      <div style={{ display: 'grid', gap: 8 }}>
        {fixtures.map(f => {
          const local = loadLocal(f.id);
          const waiting = dirtyCount(local);
          return (
            <button key={f.id} type="button" onClick={() => onPick(f.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, minHeight: 64, padding: '10px 16px', borderRadius: 14, cursor: 'pointer', textAlign: 'left',
                border: `2px solid ${f.matchDate === today ? C.blue : 'var(--border)'}`, background: 'var(--bg-card)', color: 'var(--text-primary)',
              }}>
              <div style={{ width: 130 }}>
                <div style={{ fontWeight: 700 }}>{fmtDate(f.matchDate)}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{fmtKickoff(f.kickoffAt)}</div>
              </div>
              <div style={{ flex: 1, fontSize: 18, fontWeight: 700 }}>{f.homeAway === 'H' ? 'vs' : '@'} {f.opponent}</div>
              {f.competition && <Badge>{f.competition}</Badge>}
              {f.status === 'cancelled' && <Badge color={C.red}>cancelled</Badge>}
              {f.subCount > 0 && <Badge>{f.subCount} subs</Badge>}
              {local && <Badge color={C.green}>on this device</Badge>}
              {waiting > 0 && <Badge color={C.amber}>{waiting} waiting</Badge>}
            </button>
          );
        })}
        {data && !fixtures.length && <div style={{ color: 'var(--text-muted)' }}>No fixtures between {data.from} and {data.to}.</div>}
      </div>
    </Page>
  );
}

function SyncBadge({ net, waiting, state, onSync }) {
  let color = C.green; let text; let Icon = Cloud;
  if (!net.storageOk) { color = C.red; text = 'NOT SAVED ON THIS DEVICE — storage full or blocked'; Icon = AlertTriangle; }
  else if (net.status === 'auth' || net.status === 'forbidden' || net.status === 'server' || net.status === 'notfound') {
    color = C.red; text = `${waiting ? `${waiting} changes waiting · ` : ''}${net.error}`; Icon = AlertTriangle;
  } else if (waiting > 0) {
    color = C.amber; text = `${waiting} change${waiting === 1 ? '' : 's'} waiting${net.status === 'offline' ? ' · offline' : net.status === 'syncing' ? ' · syncing…' : ''}`;
    Icon = net.status === 'offline' ? CloudOff : Cloud;
  } else if (net.status === 'offline') {
    color = C.amber; text = `Offline · all saved on this device${state?.lastSyncAt ? ` · last sync ${fmtTime(state.lastSyncAt)}` : ''}`; Icon = CloudOff;
  } else {
    text = state?.lastSyncAt ? `Synced ${fmtTime(state.lastSyncAt)}` : 'Not synced yet';
  }
  return (
    <button type="button" onClick={onSync} title="Sync now"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 14px', borderRadius: 999, border: `2px solid ${color}`, background: 'transparent', color, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
      <Icon size={18} /> {text}
    </button>
  );
}

// Offline cold start: registers public/matchday-sw.js with scope '/matchday' (it controls only
// this page; the app's own /sw.js keeps everything else), and while this page is open points the
// manifest at /matchday-manifest.json so "Add to Home Screen" opens Match Day, not the app root.
// Returns whether this page is already served by that worker (i.e. it will open offline).
function useMatchdayOffline() {
  const isOurs = () => !!navigator.serviceWorker?.controller?.scriptURL?.endsWith('/matchday-sw.js');
  const [cached, setCached] = useState(isOurs);
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const title = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prev = { href: link?.getAttribute('href'), title: title?.getAttribute('content') };
    if (link) link.setAttribute('href', '/matchday-manifest.json');
    if (title) title.setAttribute('content', 'Match Day');
    const sw = navigator.serviceWorker;
    const onChange = () => setCached(isOurs());
    if (sw) {
      sw.register('/matchday-sw.js', { scope: '/matchday' }).catch(() => {});
      sw.addEventListener('controllerchange', onChange);
    }
    return () => {
      if (link && prev.href) link.setAttribute('href', prev.href);
      if (title && prev.title) title.setAttribute('content', prev.title);
      if (sw) sw.removeEventListener('controllerchange', onChange);
      // Left Match Day in-app (e.g. browser Back): reload so the app's page isn't left running
      // under this worker — every page outside /matchday stays on the app's own /sw.js.
      setTimeout(() => {
        if (!window.location.pathname.startsWith('/matchday') && isOurs()) window.location.reload();
      }, 0);
    };
  }, []);
  return cached;
}

const TABS = [{ id: 'squad', label: 'Squad' }, { id: 'match', label: 'Match' }, { id: 'topup', label: 'Top-up' }];

function MatchView({ matchId, onBack, appCached }) {
  const { state, net, commit, syncNow, waiting } = useMatchday(matchId);
  const now = useNow();
  const wakeLock = useWakeLock();
  const [tab, setTab] = useState(null);

  const minutes = useMemo(
    () => (state ? computeMinutes({ clock: state.clock, squad: state.squad, events: state.events, now }) : null),
    [state, now]);
  const playersById = useMemo(() => new Map((state?.players || []).map(p => [p.id, p])), [state]);

  if (!state) {
    return (
      <Page>
        <Btn tone="ghost" onClick={onBack}><ArrowLeft size={18} /> Matches</Btn>
        <div style={{ marginTop: 24, fontSize: 18 }}>
          {net.status === 'syncing' || net.status === 'idle' ? 'Preparing match…' : (
            <span style={{ color: C.red, fontWeight: 700 }}>
              {net.status === 'offline' ? 'This match is not prepared on this device and there is no connection.' : net.error}
            </span>
          )}
        </div>
        {net.status !== 'syncing' && <div style={{ marginTop: 12 }}><Btn tone="primary" onClick={syncNow}>Try again</Btn></div>}
      </Page>
    );
  }

  const activeTab = tab || (minutes.phase === 'pre' && !state.squad.some(s => !s.deletedAt) ? 'squad' : 'match');
  const f = state.fixture || {};
  const dataReady = !!state.preparedAt && state.players.length > 0;
  const ready = dataReady && appCached;
  const readyText = ready ? 'Ready for offline'
    : !dataReady ? 'Not ready for offline — match data missing'
    : 'Data saved · reload once online so the page also opens offline';

  return (
    <Page>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <Btn tone="ghost" onClick={onBack}><ArrowLeft size={18} /> Matches</Btn>
        <div style={{ marginRight: 'auto' }}>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{f.homeAway === 'H' ? 'vs' : '@'} {f.opponent}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{fmtDate(f.matchDate)} · {fmtKickoff(f.kickoffAt)}</div>
        </div>
        <Badge color={ready ? C.green : dataReady ? C.amber : C.red} style={{ fontSize: 13, padding: '6px 10px' }}>
          {ready ? <CheckCircle size={14} /> : <AlertTriangle size={14} />} {readyText}
        </Badge>
        {wakeLock && <Badge style={{ fontSize: 13, padding: '6px 10px' }}>screen stays on</Badge>}
        <SyncBadge net={net} waiting={waiting} state={state} onSync={syncNow} />
      </div>
      <SessionWarning />
      {(state.rejected || []).length > 0 && (
        <div style={{ color: C.red, fontSize: 13, marginBottom: 8 }}>
          {state.rejected.length} change(s) refused by the server (latest: {state.rejected[state.rejected.length - 1].reason}).
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {TABS.map(t => (
          <Btn key={t.id} tone={activeTab === t.id ? 'primary' : 'ghost'} onClick={() => setTab(t.id)} style={{ minWidth: 110 }}>{t.label}</Btn>
        ))}
      </div>
      {activeTab === 'squad' && <SquadTab state={state} commit={commit} phase={minutes.phase} />}
      {activeTab === 'match' && <MatchTab state={state} commit={commit} minutes={minutes} playersById={playersById} now={now} />}
      {activeTab === 'topup' && <TopUpTab state={state} commit={commit} minutes={minutes} />}
    </Page>
  );
}

// Full page load on the way out: the app's pages get a fresh document under the app's own /sw.js,
// never one still controlled by the Match Day worker.
const exitToApp = () => window.location.assign('/app');

export default function MatchDay({ user }) {
  const [params, setParams] = useSearchParams();
  const matchId = Number(params.get('match')) || null;
  const appCached = useMatchdayOffline();

  if (!canUseMatchday(user)) {
    return <Page><Btn tone="ghost" onClick={exitToApp}><ArrowLeft size={18} /> App</Btn><p style={{ marginTop: 24 }}>This account has no Match Day access.</p></Page>;
  }
  if (!matchId) return <FixturePicker onPick={id => setParams({ match: String(id) })} onExit={exitToApp} />;
  return <MatchView key={matchId} matchId={matchId} appCached={appCached} onBack={() => setParams({})} />;
}
